import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["overlay", "input", "rejectionMessage"];
  static values = {
    fieldName: { type: String, default: "note[documents][]" },
    acceptMode: { type: String, default: "document" },
    maxSizeBytes: { type: Number, default: 0 },
  };

  // --- Drag events on section ---
  handleDragEnter(event) {
    event.preventDefault();
    this._showOverlay();
  }

  handleDragLeave(event) {
    // Only hide when pointer genuinely leaves the section boundary
    if (this.element.contains(event.relatedTarget)) return;
    this._hideOverlay();
  }

  // --- Drag events on overlay ---
  handleDragOver(event) {
    event.preventDefault();
  }

  handleDrop(event) {
    event.preventDefault();
    this._hideOverlay();

    const dropped = Array.from(event.dataTransfer?.files || []);
    const typeAllowed = dropped.filter((f) => this._isAllowed(f));

    if (!typeAllowed.length) {
      window.dispatchEvent(
        new CustomEvent("notification:show", {
          detail: { type: "error", message: "Unsupported file type. Please choose an allowed file." },
        })
      );
      return;
    }

    const { accepted, oversized } = this._splitBySize(typeAllowed);
    this._reportOversized(oversized);
    if (!accepted.length) return;

    this.element.dispatchEvent(
      new CustomEvent("file-upload-modal:confirm", {
        bubbles: true,
        detail: { files: accepted, fieldName: this.fieldNameValue },
      })
    );
  }

  // --- File picker (click "upload") ---
  openPicker() {
    if (this.hasInputTarget) this.inputTarget.click();
  }

  handleFileSelect(event) {
    const input = event.target;
    const picked = Array.from(input.files || []);
    const typeAllowed = picked.filter((f) => this._isAllowed(f));

    if (!typeAllowed.length) {
      this._clearNativeInput(input);
      window.dispatchEvent(
        new CustomEvent("notification:show", {
          detail: { type: "error", message: "Unsupported file type. Please choose an allowed file." },
        })
      );
      return;
    }

    const { accepted, oversized } = this._splitBySize(typeAllowed);
    this._reportOversized(oversized);
    if (!accepted.length) {
      this._clearNativeInput(input);
      return;
    }

    this.element.dispatchEvent(
      new CustomEvent("file-upload-modal:confirm", {
        bubbles: true,
        detail: { files: accepted, fieldName: this.fieldNameValue },
      })
    );
  }

  _clearNativeInput(input) {
    if (!input) return;
    try {
      input.files = new DataTransfer().files;
    } catch (_err) {
      input.value = "";
    }
  }

  // --- Overlay visibility ---
  _showOverlay() {
    if (!this.hasOverlayTarget) return;
    this.overlayTarget.classList.remove("opacity-0", "pointer-events-none");
  }

  _hideOverlay() {
    if (!this.hasOverlayTarget) return;
    this.overlayTarget.classList.add("opacity-0", "pointer-events-none");
  }

  // --- File size validation ---
  _splitBySize(files) {
    const accepted = [];
    const oversized = [];
    files.forEach((f) => (this._isWithinSize(f) ? accepted.push(f) : oversized.push(f)));
    return { accepted, oversized };
  }

  _isWithinSize(file) {
    if (!this.hasMaxSizeBytesValue || this.maxSizeBytesValue <= 0) return true;
    return file.size <= this.maxSizeBytesValue;
  }

  _reportOversized(files) {
    if (!files.length) {
      this._clearRejection();
      return;
    }

    const maxMb = Math.round(this.maxSizeBytesValue / 1024 / 1024);
    const names = files.map((f) => f.name).join(", ");
    const message = `File too large (max ${maxMb}MB): ${names}.`;

    if (this.hasRejectionMessageTarget) {
      this.rejectionMessageTarget.textContent = message;
      this.rejectionMessageTarget.classList.remove("hidden");
    } else {
      window.dispatchEvent(
        new CustomEvent("notification:show", {
          detail: { type: "error", message },
        })
      );
    }
  }

  _clearRejection() {
    if (!this.hasRejectionMessageTarget) return;
    this.rejectionMessageTarget.textContent = "";
    this.rejectionMessageTarget.classList.add("hidden");
  }

  // --- File type validation ---
  _isAllowed(file) {
    const mode = (this.acceptModeValue || "document").toLowerCase();
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const type = file.type.toLowerCase();

    // Business-approved file types
    const ALLOWED = {
      image: {
        exts: new Set(["png", "jpg", "jpeg", "heic", "heif", "bmp", "gif", "tiff", "tif"]),
        mimes: new Set(["image/png", "image/jpeg", "image/heic", "image/heif", "image/bmp", "image/gif", "image/tiff"]),
      },
      video: {
        exts: new Set(["mp4", "mov"]),
        mimes: new Set(["video/mp4", "video/quicktime"]),
      },
      document: {
        exts: new Set(["doc", "docx", "pdf", "odt", "pages", "txt"]),
        mimes: new Set([
          "application/pdf",
          "text/plain",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
          "application/vnd.oasis.opendocument.text",
          "application/vnd.apple.pages",
        ]),
      },
      spreadsheet: {
        exts: new Set(["xlsx", "xls", "xlsm", "csv", "numbers"]),
        mimes: new Set([
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          "application/vnd.ms-excel",
          "application/vnd.ms-excel.sheet.macroenabled.12",
          "text/csv",
          "application/vnd.apple.numbers",
        ]),
      },
      presentation: {
        exts: new Set(["ppt", "pptx", "key"]),
        mimes: new Set([
          "application/vnd.ms-powerpoint",
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
          "application/vnd.apple.keynote",
        ]),
      },
      other: {
        exts: new Set(["msg", "vcf"]),
        mimes: new Set(["application/vnd.ms-outlook", "text/vcard", "text/x-vcard"]),
      },
    };

    const matches = ({ exts, mimes }) => exts.has(ext) || mimes.has(type);
    const isImage = matches(ALLOWED.image);
    const isAny = Object.values(ALLOWED).some(matches);

    if (mode === "image") return isImage;
    if (mode === "document" || mode === "all") return isAny;

    return false;
  }
}
